import { useState, useEffect, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { VisitsService } from '@services/requests/housien-services/VisitsService';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useDataStateOverride } from '@/shared/ui';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { buildPropertiesMap } from './buildPropertiesMap';
import {
  STATUS_MAP_REVERSE,
  applyVisitsOverride,
  runVisitsFetch,
} from './useVisitsKanban.helpers';
import type {
  KanbanVisit,
  VisitKanbanStatus,
  UseVisitsKanbanReturn,
} from './useVisitsKanban.helpers';

export { STATUS_MAP_REVERSE };
export type { KanbanVisit, VisitKanbanStatus, UseVisitsKanbanReturn };

/**
 * Fetches the agency's visits for the given date window into kanban shape,
 * toggling loading/error state and rebuilding the property lookup map. Re-runs
 * when the agency, properties, date range, or refetch tick changes.
 *
 * @param agencyId - Active agency id; fetch is skipped when absent.
 * @param agencyProperties - Agency properties used to resolve labels/addresses.
 * @param startDate - Inclusive window start (ISO date).
 * @param endDate - Inclusive window end (ISO date).
 * @param fetchTick - Monotonic counter that forces a refetch when incremented.
 * @param setters - State setters for visits, error, and loading.
 */
function useKanbanFetch(
  agencyId: string | undefined,
  agencyProperties: unknown,
  startDate: string | undefined,
  endDate: string | undefined,
  fetchTick: number,
  setters: {
    setVisits: (v: KanbanVisit[]) => void;
    setError: (e: string | null) => void;
    setIsLoading: (v: boolean) => void;
  },
) {
  useEffect(() => {
    if (!agencyId) return;
    setters.setIsLoading(true);
    setters.setError(null);
    return runVisitsFetch({
      fetchVisits: () => VisitsService.getPropertyVisits(agencyId, 1, 100, startDate, endDate),
      buildMap: () => buildPropertiesMap(agencyId, agencyProperties as any),
      onSuccess: setters.setVisits,
      onError: setters.setError,
      onSettled: () => setters.setIsLoading(false),
      fallbackError: 'error.loadFailed',
    });
  }, [agencyId, agencyProperties, fetchTick, startDate, endDate]);
}

/**
 * Builds a stable callback that optimistically moves a visit to a new status,
 * persists it via the API, and refetches to reconcile on failure.
 *
 * @param agencyId - Active agency id; the callback is a no-op when absent.
 * @param refetch - Refetch trigger invoked to roll back on persistence errors.
 * @param setVisits - State setter used for the optimistic update.
 * @returns An async status-change handler accepting the visit id, new status, and optional extra fields.
 */
function useUpdateStatus(
  agencyId: string | undefined,
  refetch: () => void,
  setVisits: Dispatch<SetStateAction<KanbanVisit[]>>,
) {
  return useCallback(async (
    visitId: string, newStatus: VisitKanbanStatus, extraFields?: Record<string, unknown>,
  ) => {
    if (!agencyId) return;
    setVisits((prev) => prev.map((v) => (v.id === visitId ? { ...v, status: newStatus } : v)));
    try {
      await VisitsService.updateVisit(agencyId, visitId, {
        status: STATUS_MAP_REVERSE[newStatus],
        ...extraFields,
      });
    } catch {
      notifications.show({ color: 'red', message: 'error.statusUpdateFailed' });
      refetch();
    }
  }, [agencyId, refetch]);
}

/**
 * Kanban board data hook: fetches the agency's visits for an optional date
 * window, exposes optimistic status updates, and applies any active dev
 * data-state override.
 *
 * @param startDate - Optional inclusive window start (ISO date).
 * @param endDate - Optional inclusive window end (ISO date).
 * @returns The visits, loading/error state, and the refetch/updateStatus actions.
 */
export function useVisitsKanban(startDate?: string, endDate?: string): UseVisitsKanbanReturn {
  const { agency, properties: agencyProperties } = useAgency((s) => s);
  const [visits, setVisits] = useState<KanbanVisit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTick, setFetchTick] = useState(0);
  const override = useDataStateOverride();

  const refetch = useCallback(() => setFetchTick((n) => n + 1), []);

  useKanbanFetch(agency.id, agencyProperties, startDate, endDate, fetchTick, {
    setVisits, setError, setIsLoading,
  });

  const updateStatus = useUpdateStatus(agency.id, refetch, setVisits);

  return applyVisitsOverride(override, { visits, isLoading, error }, { refetch, updateStatus });
}
